// Tells every other open tab of this site when someone logs in or out.
//
// Why: the login is an httpOnly cookie, and ALL tabs in the same browser
// share one cookie. If an admin is open in tab 1 and a recruiter logs in
// in tab 2, the cookie now belongs to the recruiter, so tab 1's API calls
// return the recruiter's data while its Redux store (and the top nav) still
// shows the admin. Broadcasting the change lets tab 1 reload itself as the
// new user straight away instead of showing a mix of both.
//
// Uses BroadcastChannel (all modern browsers) plus a localStorage "storage"
// event as a fallback. A tab never receives its own message.

const CHANNEL_NAME = "lonewolf-session";
const STORAGE_KEY = "lonewolf-session-event";

const channel = typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel(CHANNEL_NAME) : null;

// type: "login" | "logout"; userId: the newly signed-in user's id (login only)
export function announceSessionChange(type, userId = null) {
  const message = { type, userId: userId ? String(userId) : null, at: Date.now() };
  try {
    if (channel) channel.postMessage(message);
  } catch {
    /* ignore */
  }
  try {
    // A new value every time (the timestamp), so the "storage" event always fires.
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
  } catch {
    /* private mode / storage blocked — BroadcastChannel still covers it */
  }
}

// Calls `onChange(message)` when ANOTHER tab logs in or out.
// Returns an unsubscribe function.
export function subscribeToSessionChanges(onChange) {
  let lastAt = 0;
  // Both transports can deliver the same message — handle it once.
  const handle = (message) => {
    if (!message || !message.type || message.at === lastAt) return;
    lastAt = message.at;
    onChange(message);
  };

  const onChannelMessage = (event) => handle(event.data);
  const onStorage = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      handle(JSON.parse(event.newValue));
    } catch {
      /* ignore malformed value */
    }
  };

  if (channel) channel.addEventListener("message", onChannelMessage);
  window.addEventListener("storage", onStorage);

  return () => {
    if (channel) channel.removeEventListener("message", onChannelMessage);
    window.removeEventListener("storage", onStorage);
  };
}
